import { createColumnsLayout } from 'src/helpers/columnsLayoutUtils';
import { DEFAULT_DIVIDER_BLOCK_SPACING } from 'src/helpers/blockSpacing';
import {
  emptyLocalizedString,
  filledLocalizedString,
} from 'src/helpers/localizedStringUtils';
import {
  IColumnsBlock,
  IPageBlock,
  ITextBlock,
  PageBlockType,
} from 'src/interfaces/PageBlock';

const emptyI18n = emptyLocalizedString;

export const ALL_PAGE_BLOCK_TYPES: PageBlockType[] = [
  'text',
  'divider',
  'columns',
  'image',
  'logo',
  'calendar',
  'countdown',
  'button',
  'trackMap',
  'map',
  'sponsors',
  'competitionsList',
  'results',
  'signupFlow',
  'signupBreakdown',
];

/** Block types allowed inside column cells; excludes nested columns. */
export const NESTED_BLOCK_TYPES: PageBlockType[] = ALL_PAGE_BLOCK_TYPES.filter(
  type => type !== 'columns',
);

export function newPageBlockId(): string {
  return crypto.randomUUID();
}

export function createEmptyPageBlock(
  type: PageBlockType,
  sortOrder: number,
  options?: { trackMapId?: string; columnCount?: 2 | 3 | 4 },
): IPageBlock {
  const base = { id: newPageBlockId(), sortOrder };
  switch (type) {
    case 'text':
      return { ...base, type: 'text', i18n: { en: '', hu: '', ro: '' } };
    case 'divider':
      return { ...base, type: 'divider', spacing: DEFAULT_DIVIDER_BLOCK_SPACING };
    case 'trackMap':
      return { ...base, type: 'trackMap', trackMapId: options?.trackMapId ?? '' };
    case 'map':
      return {
        ...base,
        type: 'map',
        center: [46.586922, 25.082857],
        zoom: 13,
        height: 320,
        pins: [],
      };
    case 'sponsors':
      return { ...base, type: 'sponsors' };
    case 'competitionsList':
      return { ...base, type: 'competitionsList', columnCount: 4 };
    case 'results':
      return { ...base, type: 'results' };
    case 'image':
      return {
        ...base,
        type: 'image',
        mode: 'single',
        alt: emptyI18n(),
        display: {},
      };
    case 'logo':
      return { ...base, type: 'logo', alt: { en: '', hu: '', ro: '' }, maxHeight: 200 };
    case 'calendar':
      return { ...base, type: 'calendar', useEditionDate: true };
    case 'countdown':
      return {
        ...base,
        type: 'countdown',
        useEditionDate: true,
        renderMap: [true, true, false, false],
      };
    case 'button':
      return {
        ...base,
        type: 'button',
        label: emptyI18n(),
        href: filledLocalizedString('/race/signup'),
        variant: 'contained',
        size: 'large',
      };
    case 'columns': {
      const columnCount = options?.columnCount ?? 2;
      const layout = createColumnsLayout(columnCount);
      return { ...base, type: 'columns', ...layout } satisfies IColumnsBlock;
    }
    case 'signupFlow':
      return { ...base, type: 'signupFlow' };
    case 'signupBreakdown':
      return { ...base, type: 'signupBreakdown' };
    default:
      return { ...base, type: 'text', i18n: { en: '', hu: '', ro: '' } } satisfies ITextBlock;
  }
}

export function createDefaultHomeHeroSectionBlocks(): {
  column0: IPageBlock[];
  column1: IPageBlock[];
} {
  return {
    column0: [createEmptyPageBlock('logo', 0)],
    column1: [
      createEmptyPageBlock('calendar', 0),
      createEmptyPageBlock('countdown', 1),
      createEmptyPageBlock('button', 2),
    ],
  };
}
